import { useState, useEffect, useContext, useRef, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import moment from 'moment'

import { DocumentsContext, FolderContext, UserContext } from '../../context'
import { FEATURE, timeDisplayFormat, dateOnlyDisplayFormat, isFeatureEnabled, CLIENT_SPECIFIC_TEMPLATES_FOLDER_ID } from '../../utils'
import CustomTooltip from './CustomTooltip'
import { useAgencyActions, useSingleDocumentActions, useTemplatesActions } from '../../hooks'
import { Folder, Signature, CalendarAlt, History, Pen, Download, Categories, MoveToFolder } from '../../assets/icons';
import { AdminFeatureWrapper } from '../utils/AdminFeatureWrapper'
import ActionsDropdown from '../UI/ActionsDropdown'
import { urlSuffixForEnvironment } from '../../utils'
import { AccountTree, DeleteForever, Edit, Publish, Share, Visibility, VisibilityOff, AttachFile, InfoOutlined, Add } from '@material-ui/icons'
import config from '../../config.json'
import SectionsPopup from '../sections/SectionsPopup'
import AttachmentsPopup from '../sections/AttachmentsPopup'
import SignaturesPopup from '../sections/SignaturesPopup'
import { Delete, MoreVert, OpenInNew, SellOutlined } from '@mui/icons-material';
import { MoveSelectedItemsToFolderModal } from '../misc';
import TemplateTagsPopup from '../sections/TemplateTagsPopup'
import Switch from './Switch'
import Checkbox from './Checkbox'

const TemplatesTableRow = ({
  id,
  template,
  onPreview,
  onCreateDocument,
  standard,
  onTemplateEdit,
  onTemplateDelete,
  onTemplateToggleHidden,
  onInfo,
  onHistory,
  onManageAttachments,
  onToggleValidation,
  selected,
  onSelectToggle
}) => {
  const { partner, user, userClaims, integration } = useContext(UserContext)
  const { moveTemplateToFolder } = useTemplatesActions()
  const { folders } = useContext(FolderContext)
  const { isTemplateValidationRequired } = useAgencyActions()

  const [createdAt, setCreatedAt] = useState('')
  const [createdAtTime, setCreatedAtTime] = useState('')
  const [modifiedAt, setModifiedAt] = useState('')
  const [modifiedAtTime, setModifiedAtTime] = useState('')
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSectionsPopup, setShowSectionsPopup] = useState(false);
  const [showTemplateTagsPopup, setShowTemplateTagsPopup] = useState(false);

  const [showAttachmentsPopup, setShowAttachmentsPopup] = useState(false);
  const [showSignaturesPopup, setShowSignaturesPopup] = useState(false);
  const [showMoveToFolderModal, setShowMoveToFolderModal] = useState(false);
  const [hideDropdown, setHideDropdown] = useState(false);

  const sectionsPopupEl = useRef();
  const sectionsTagsPopupEl = useRef();
  const attachmentsPopupEl = useRef();
  const signaturesPopupEl = useRef();


  // Set metadata
  useEffect(() => {
    if (template?.meta?.created) {
      setCreatedAt(moment(template.meta.created).format(dateOnlyDisplayFormat))
      setCreatedAtTime(moment(template.meta.created).format(timeDisplayFormat))
    } else if (template.createdAt) {
      setCreatedAt(moment(template.createdAt).format(dateOnlyDisplayFormat))
      setCreatedAtTime(moment(template.createdAt).format(timeDisplayFormat))
    }
    if (template?.meta?.updated) {
      setModifiedAt(moment(template.meta.updated).format(dateOnlyDisplayFormat))
      setModifiedAtTime(moment(template.meta.updated).format(timeDisplayFormat))
    } else if (template.updatedAt) {
      setModifiedAt(moment(template.updatedAt).format(dateOnlyDisplayFormat))
      setModifiedAtTime(moment(template.updatedAt).format(timeDisplayFormat))
    }
  }, [template])


  // On preview modal open
  const handleOpenPreviewModal = () => {
    onPreview(template)
  }
  // On create document
  const handleCreateDocument = () => {
    onCreateDocument(template)
  }

  // On info modal open
  const handleOpenInfoModal = () => {
    // Workaround for hiding dropdown after opening information modal
    setHideDropdown(true);
    setTimeout(() => {
      setHideDropdown(false);
    }, 500);

    onInfo(template)
  }

  const handleOpenHistoryModal = () => {
    onHistory(template)
  }

  const shareTemplateClickHandler = (tmpl) => {
    window.open(`https://jurisur-document${urlSuffixForEnvironment(config.environment).replace('_', '-')}.web.app/document/${tmpl.id}?token=${user.clientAccessToken}`, '_blank');
  }

  const singleTemplateDownload = (tmpl) => {
    const a = document.createElement("a");
    a.setAttribute('download', tmpl.name);
    a.setAttribute('href', `${tmpl.templateUrl}`);
    document.body.appendChild(a);
    a.click();
  }

  // drag and drop template to folder
  const handleDragStart = (e) => {
    e.dataTransfer.setData('template', JSON.stringify(template));
    e.dataTransfer.setData('view', 'templates');
  };

  // On open move to folder
  const handleOpenMoveToFolderModal = (e, template) => {
    e.preventDefault()
    setShowMoveToFolderModal(true)
  }

  // On close move to folder
  const handleCloseMoveToFolderModal = () => {
    setShowMoveToFolderModal(false)
  }

  const handleTemplateMove = async (folder) => {
    await moveTemplateToFolder({ folder, template, view: "standard-templates" })
    handleCloseMoveToFolderModal()
  }

  const canMoveTemplateToFolder = (template, folder) => {
    if (folder?.id === CLIENT_SPECIFIC_TEMPLATES_FOLDER_ID) {
      return false
    }
    if (template.folderId.includes(CLIENT_SPECIFIC_TEMPLATES_FOLDER_ID)) {
      return false
    }
    return true
  }

  return (
    <div className={`documents-table-row !pl-0 no-cursor`}
      draggable={isFeatureEnabled(FEATURE.TEMPLATE_MANAGEMENT, integration) && canMoveTemplateToFolder(template)}
      onDragStart={handleDragStart}
      style={{ cursor: isFeatureEnabled(FEATURE.TEMPLATE_MANAGEMENT, integration) && canMoveTemplateToFolder(template) ? 'move' : 'default' }}
    >

      <div className="documents-table-column !flex-grow !pl-0">
        <div className="flex gap-2 px-3">
          {/* <OpenInNew className='link-icon' onClick={handleCreateDocument} /> */}
          <Checkbox checked={selected} onChange={onSelectToggle} />
          <ActionsDropdown
            trigger={<button className="icon-btn icon-btn--transparent icon-btn--svg-xl more-vert-icon" onClick={() => setShowDropdown(!showDropdown)}><span className="svg-icon"><MoreVert fontSize='medium' /></span></button>}
            hideHeader
            width={120}
            dropdownClass="actions-dropdown--no-padding"
            hide={hideDropdown}
          >
            <ul className="dashboard-column__dropdown !border-b-0">
              {!standard && <li onClick={handleCreateDocument}><span className="icon add"><Add /></span>Créer un document</li>}
              {!standard && <li onClick={handleOpenInfoModal}><span className="icon rotate-180"><InfoOutlined /></span>Informations</li>}
              {!standard && <li onClick={handleOpenHistoryModal}><span className="icon rotate-180"><History /></span>Historique</li>}
              {isFeatureEnabled(FEATURE.TEMPLATE_MANAGEMENT, integration) && <li onClick={() => onTemplateEdit(template)} className="edit"><span className="icon"><Pen /></span>Renommer</li>}
              {!standard && isFeatureEnabled(FEATURE.TEMPLATE_MANAGEMENT, integration) && <li onClick={() => singleTemplateDownload(template)}><span className="icon"><Download /></span>Télécharger</li>}
              {!standard && isFeatureEnabled(FEATURE.TEMPLATE_MANAGEMENT, integration) &&
                  <ActionsDropdown
                    trigger={
                      <li>
                        <div className="flex gap-4"><span className="icon"><Categories /></span>Catégories</div>
                      </li>
                    }
                    onTriggerClick={() => setShowSectionsPopup(true)}
                    hideHeader
                    dropdownClass="actions-dropdown--no-padding folders-popup-dropdown"
                    width={300}
                    onClose={() => setShowSectionsPopup(false)}
                    hide={!showSectionsPopup}
                    hideArrow={true}
                  >
                    <SectionsPopup
                      ref={sectionsPopupEl}
                      onCancel={() => setShowSectionsPopup(false)}
                      resource={template}
                    />
                  </ActionsDropdown>}
              {!standard && isFeatureEnabled(FEATURE.TEMPLATE_TAG_MANAGEMENT, integration) &&
                <ActionsDropdown
                  trigger={
                    <li>
                      <div className="flex gap-4"><span className="icon"><SellOutlined /></span>Tags</div>
                    </li>
                  }
                  onTriggerClick={() => setShowTemplateTagsPopup(true)}
                  hideHeader
                  dropdownClass="actions-dropdown--no-padding folders-popup-dropdown"
                  width={340}
                  maxWidth={340}
                  onClose={() => setShowTemplateTagsPopup(false)}
                  hide={!showTemplateTagsPopup}
                  hideArrow={true}
                >
                  <TemplateTagsPopup
                    ref={sectionsTagsPopupEl}
                    onCancel={() => setShowTemplateTagsPopup(false)}
                    resource={template}
                  />
                </ActionsDropdown>}
              {!standard && isFeatureEnabled(FEATURE.HIDDEN_TEMPLATES, integration) && <li onClick={() => onTemplateToggleHidden(template)}><span className="icon">{template.hidden ? <Visibility /> : <VisibilityOff />}</span>{template.hidden ? 'Rendre disponible' : 'Masquer'}</li>}
              {!standard && isFeatureEnabled(FEATURE.TEMPLATE_SHARE , integration) && <li onClick={() => shareTemplateClickHandler(template)} className="edit"><span className="icon"><Share /></span>Partager</li>}

              {!standard && isFeatureEnabled(FEATURE.TEMPLATE_MANAGEMENT, integration) && 
                <ActionsDropdown
                  trigger={
                  <li>
                    <div className="flex gap-4 items-center"><span className="icon template-box"><Signature /></span>
                      <span>{'Paramètres signature électronique'}</span>
                    </div>
                  </li>}
                  onTriggerClick={() => setShowSignaturesPopup(true)}
                  hideHeader
                  dropdownClass="actions-dropdown--no-padding folders-popup-dropdown"
                  width={300}
                  onClose={() => setShowSignaturesPopup(false)}
                  hide={!showSignaturesPopup}
                  hideArrow={true}
                >
                  <SignaturesPopup
                    ref={signaturesPopupEl}
                    onCancel={() => setShowSignaturesPopup(false)}
                    resource={template}
                  />
                </ActionsDropdown>}
              {(isFeatureEnabled(FEATURE.TEMPLATE_MANAGEMENT, integration)  && isFeatureEnabled(FEATURE.DEFAULT_ATTACHMENTS, integration)) &&
                <li onClick={() => onManageAttachments(template)}>
                  <div className="flex gap-4"><span className="icon"><AttachFile /></span>Annexes</div>
                </li>
              }
              {isFeatureEnabled(FEATURE.TEMPLATE_MANAGEMENT, integration)  && canMoveTemplateToFolder(template) && <li onClick={(e) => handleOpenMoveToFolderModal(e, template)}><span className="icon"><MoveToFolder /></span>Déplacer</li>}
            </ul>
            {isFeatureEnabled(FEATURE.TEMPLATE_MANAGEMENT, integration) && <ul className="dashboard-column__dropdown">
              <li className='dashboard-column__dropdown--trash' onClick={() => onTemplateDelete(template)}><span className="icon"><Delete /></span>Supprimer</li>
            </ul>}
          </ActionsDropdown>
        </div>
        <CustomTooltip content={template.name} position='top'>
          <p className='doc_name cursor-pointer line-clamp-2' onClick={handleCreateDocument}> &nbsp;{template.name}</p>
        </CustomTooltip>
      </div>
      {isFeatureEnabled(FEATURE.HIDDEN_TEMPLATES, integration) &&
        <div className="documents-table-column documents-table-column__status">
          {!template.hidden && <span className='active-text'>Actif</span>}
          {template.hidden && <span className='hidden-text'>
            <VisibilityOff />
            Masqué</span>}
        </div>
      }
      <div className="documents-table-column documents-table-column__date">
        <div className="w-full">
          <p className='date'>{createdAt}</p>
          <p className='time'>{createdAtTime}</p>
        </div>
      </div>
      <div className="documents-table-column documents-table-column__date">
        <div className="w-full">
          <p className='date'>{modifiedAt}</p>
          <p className='time'>{modifiedAtTime}</p>
        </div>
      </div>
      {isFeatureEnabled(FEATURE.DOCUMENT_VALIDATION, integration) && user.validation_access === 'manager' &&
        <div className="documents-table-column documents-table-column__validation">
          <div className="w-full">
            <Switch checked={isTemplateValidationRequired(template.id)} onChange={() => onToggleValidation(template)} />
          </div>
        </div>
      }
      <div className="documents-table-column documents-table-column__history">
        {(template.versionNotes && template.versionNotes.length > 0 && !standard) && <CustomTooltip content="Historique"><button className="icon-btn icon-btn--transparent icon-btn--svg-md mx-auto" onClick={handleOpenHistoryModal}><CalendarAlt /></button></CustomTooltip>}
      </div>

      {showMoveToFolderModal && (
        <MoveSelectedItemsToFolderModal
          onClose={handleCloseMoveToFolderModal}
          folders={folders}
          items={[template]}
          currentFolder={null}
          newFolderCreated={false} // remove this, improve
          onNewFolderCreated={() => { }} // remove this, improve
          onMove={handleTemplateMove}
        />
      )}
    </div>
  )
}

export default TemplatesTableRow